<template>
  <div>
    <!-- Role Selector Component -->
    <RoleSelector 
      ref="roleSelector"
      @role-selected="onRoleSelected"
    />
    
    <div v-if="deviceType == 'Mobile'" class="mobile-container">
      <div class="mobile-sidebar">
        <div class="mobile-header" :style="{ background: currentSidebarBackground }">
          <img src="@/assets/img/logo_lan.png" alt="Logo" class="mobile-logo">
          <RoleDisplay @show-role-selector="handleShowRoleSelector" />
        </div>
        
        <div class="mobile-menu">
          <div class="menu-item" v-for="(item, index) in filteredOptionsMobile" :key="index" @click="getViews(`${item.link}`)">
            <div class="menu-icon">
              <font-awesome-icon :icon="item.icon" />
            </div>
            <span class="menu-text">{{ item.nama_menu }}</span>
            <div class="menu-arrow">
              <font-awesome-icon icon="chevron-right" />
            </div>
          </div>
          
          <div class="menu-item logout-item" @click="logout">
            <div class="menu-icon">
              <font-awesome-icon icon="sign-out-alt" />
            </div>
            <span class="menu-text">Logout</span>
            <div class="menu-arrow">
              <font-awesome-icon icon="chevron-right" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="deviceType == 'Desktop'" class="desktop-container">
      <!-- Overlay for mobile sidebar -->
      <div 
        v-if="showMobileSidebar" 
        class="sidebar-overlay"
        @click="toggleSidebar"
      ></div>
      
      <el-container class="h-screen">
        <!-- Toggle Button for Mobile/Tablet -->
        <button 
          class="sidebar-toggle-btn"
          @click="toggleSidebar"
          :class="{ 'active': showMobileSidebar }"
        >
          <span class="toggle-line"></span>
          <span class="toggle-line"></span>
          <span class="toggle-line"></span>
        </button>
        
        <el-aside 
          :width="sidebarWidth" 
          class="desktop-sidebar"
          :class="{ 
            'sidebar-collapsed': isCollapsed,
            'sidebar-mobile-open': showMobileSidebar || isDesktopView,
            'sidebar-mobile-closed': !showMobileSidebar && !isDesktopView
          }"
          :style="{ background: currentSidebarBackground }"
        >
          <div class="sidebar-header">
            <img 
              src="@/assets/img/logo_lan.png" 
              alt="Logo" 
              class="sidebar-logo"
              :class="{ 'logo-collapsed': isCollapsed }"
            >
            <div class="sidebar-divider" v-if="!isCollapsed"></div>
          </div>
          
          <div class="role-section" v-if="!isCollapsed">
            <RoleDisplay @show-role-selector="handleShowRoleSelector" />
          </div>
          
          <!-- Collapse Toggle for Desktop -->
          <button 
            class="collapse-btn"
            @click="toggleCollapse"
            v-if="!isMobileView"
            :title="isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'"
          >
            <font-awesome-icon 
              :icon="isCollapsed ? 'chevron-right' : 'chevron-left'" 
            />
          </button>
          
          <nav class="sidebar-nav">
            <ul class="nav-list">
              <li v-for="(item, index) in filteredOptions" :key="index" class="nav-item">
                <div 
                  class="nav-link" 
                  @click="getViews(`${item.link}`)" 
                  :class="{ 'active': $route.path === item.link, 'collapsed': isCollapsed }"
                  :title="isCollapsed ? item.nama_menu : ''"
                >
                  <div class="nav-icon">
                    <font-awesome-icon :icon="item.icon" />
                  </div>
                  <span class="nav-text" v-if="!isCollapsed">{{ item.nama_menu }}</span>
                  <div class="nav-indicator"></div>
                </div>
              </li>
              
              <li class="nav-item logout-nav">
                <div 
                  class="nav-link logout-link" 
                  @click="logout"
                  :class="{ 'collapsed': isCollapsed }"
                  :title="isCollapsed ? 'Logout' : ''"
                >
                  <div class="nav-icon">
                    <font-awesome-icon icon="sign-out-alt" />
                  </div>
                  <span class="nav-text" v-if="!isCollapsed">Logout</span>
                </div>
              </li>
            </ul>
          </nav>
        </el-aside>
        
        <el-main class="main-content" :class="{ 'content-expanded': isCollapsed }">
          <router-view></router-view>
        </el-main>
      </el-container>
    </div>
  </div>
</template>

<script>
import ingredient from '@/assets/json/ingredient.json'
import RoleSelector from '@/components/RoleSelector.vue'
import RoleDisplay from '@/components/RoleDisplay.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    RoleSelector,
    RoleDisplay
  },
  props: {
    'deviceType': String
  },
  data() {
    return {
      options: ingredient.menus,
      options_mobile: ingredient.menus_mobile,
      isCollapsed: false,
      showMobileSidebar: false,
      screenWidth: window.innerWidth,
      sidebarColorIndex: 0,
      backgroundColors: [
        {
          gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          name: 'Purple Blue'
        },
        {
          gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          name: 'Pink Red'
        },
        {
          gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          name: 'Blue Cyan'
        },
        {
          gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          name: 'Green Mint'
        },
        {
          gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          name: 'Pink Yellow'
        },
        {
          gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          name: 'Mint Pink'
        },
        {
          gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
          name: 'Coral Pink'
        },
        {
          gradient: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
          name: 'Purple Rose'
        },
        {
          gradient: 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 100%)',
          name: 'Peach Lavender'
        },
        {
          gradient: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
          name: 'Mint Blue'
        },
        {
          gradient: 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)',
          name: 'Red Purple'
        },
        {
          gradient: 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)',
          name: 'Gold Teal'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['currentRole', 'isAdmin', 'canAccessAdminFeatures']),
    
    filteredOptions() {
      return this.options.filter(menu => {
        return this.canAccessMenu(menu);
      });
    },
    
    filteredOptionsMobile() {
      return this.options_mobile.filter(menu => {
        return this.canAccessMenu(menu);
      });
    },
    
    currentUsername() {
      return this.$store.state.info?.sub || 'Unknown';
    },
    
    sidebarWidth() {
      if (this.isMobileView) return '100%';
      if (this.isTabletView) return '280px';
      return this.isCollapsed ? '80px' : '320px';
    },
    
    isMobileView() {
      return this.screenWidth <= 768;
    },
    
    isTabletView() {
      return this.screenWidth > 768 && this.screenWidth <= 1024;
    },
    
    isDesktopView() {
      return this.screenWidth > 1024;
    },
    
    currentSidebarBackground() {
      return this.backgroundColors[this.sidebarColorIndex].gradient;
    },
    
    currentColorName() {
      return this.backgroundColors[this.sidebarColorIndex].name;
    }
  },
  methods: {
    ...mapActions(['get_roles']),
    
    getViews: function(value) {
      if (this.$route.path !== value) {
        this.$router.push(value);
      }
    },
    
    logout: function () {
      this.$store.dispatch("logout").then(() => {
        this.$router.push({ name: "home" });
      });
    },
    
    canAccessMenu(menu) {
      // If menu has no role restriction, show it to all authenticated users
      if (!menu.role && !menu.required_roles) {
        return true;
      }
      
      // Check specific required roles first
      if (menu.required_roles && Array.isArray(menu.required_roles)) {
        return menu.required_roles.includes(this.currentRole);
      }
      
      // Fallback to legacy role check
      if (menu.role === 'admin') {
        return this.canAccessAdminFeatures;
      }
      
      // For other specific roles
      if (menu.role && this.currentRole) {
        return this.currentRole === menu.role || this.currentRole === `ROLE_${menu.role.toUpperCase()}`;
      }
      
      return false;
    },
    
    onRoleSelected(selectedRole) {
      console.log('Role selected:', selectedRole);
      // Role has been selected, user can now access the interface
      // The store has been updated with the active role
    },

    handleShowRoleSelector() {
      // Called when user clicks role switcher button
      this.$refs.roleSelector.showManually()
    },
    
    toggleSidebar() {
      this.showMobileSidebar = !this.showMobileSidebar;
    },
    
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    
    handleResize() {
      this.screenWidth = window.innerWidth;
      
      // Auto-collapse on tablet only
      if (this.isTabletView) {
        this.showMobileSidebar = false;
      }
      
      // Auto-expand on desktop
      if (this.isDesktopView) {
        this.showMobileSidebar = false;
        if (this.screenWidth > 1200) {
          this.isCollapsed = false;
        }
      }
      
      // Close mobile sidebar when switching to larger screens
      if (!this.isMobileView && this.showMobileSidebar) {
        this.showMobileSidebar = false;
      }
    },
    
    closeSidebarOnNavigation() {
      if (this.isMobileView) {
        this.showMobileSidebar = false;
      }
    },
    
    randomizeSidebarColor() {
      const oldIndex = this.sidebarColorIndex;
      this.sidebarColorIndex = Math.floor(Math.random() * this.backgroundColors.length);
      console.log(`Color changed from index ${oldIndex} to ${this.sidebarColorIndex} (${this.currentColorName})`);
    },
    
    applySidebarColor() {
      this.saveSidebarColor();
      // Apply to desktop sidebar
      const desktopSidebar = document.querySelector('.desktop-sidebar');
      if (desktopSidebar) {
        desktopSidebar.style.background = this.currentSidebarBackground;
      }
      // Apply to mobile sidebar header
      const mobileHeader = document.querySelector('.mobile-header');
      if (mobileHeader) {
        mobileHeader.style.background = this.currentSidebarBackground;
      }
    },
    
    saveSidebarColor() {
      localStorage.setItem('sidebarColorIndex', this.sidebarColorIndex.toString());
    },
    
    loadSidebarColor() {
      // Always randomize on every page load/refresh - ignore localStorage
      this.randomizeSidebarColor();
    },
    
    applySidebarColor() {
      // Force reactivity update by using nextTick
      this.$nextTick(() => {
        // Apply background through reactive computed property
        // The :style binding should handle this automatically
        this.saveSidebarColor();
      });
    }
  },
  
  mounted() {
    window.addEventListener('resize', this.handleResize);
    
    // Initial setup based on screen size
    this.handleResize();
    
    // Initialize random sidebar color
    this.loadSidebarColor();
    
    // Apply color after DOM is ready
    this.$nextTick(() => {
      this.applySidebarColor();
    });
    
    // Save color before page unload
    window.addEventListener('beforeunload', () => {
      this.saveSidebarColor();
    });
    
    // Close sidebar when navigating on mobile
    this.$router.afterEach(() => {
      this.closeSidebarOnNavigation();
    });
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  
  watch: {
    sidebarColorIndex: {
      handler() {
        this.applySidebarColor();
      },
      immediate: false
    }
  },
  
  async created() {
    // Ensure roles are loaded when component is created
    if (this.$store.getters.isLoggedIn && (!this.$store.state.userRoles || Object.keys(this.$store.state.userRoles).length === 0)) {
      await this.get_roles();
    }
  },
}
</script>

<style scoped>
/* Desktop Sidebar Styles */
.desktop-container {
  height: 100vh;
  overflow: hidden;
  position: relative;
}

.desktop-container .el-container {
  background: transparent !important;
}

.desktop-container .el-aside {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

/* Override Element UI default styles */
.el-container {
  background-color: transparent !important;
}

.el-aside {
  background-color: transparent !important;
}

.el-main {
  background-color: transparent !important;
}

/* Sidebar Overlay for Mobile */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
}

/* Toggle Button */
.sidebar-toggle-btn {
  position: fixed;
  top: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.sidebar-toggle-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.toggle-line {
  width: 20px;
  height: 2px;
  background: white;
  border-radius: 1px;
  transition: all 0.3s ease;
}

.sidebar-toggle-btn.active .toggle-line:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.sidebar-toggle-btn.active .toggle-line:nth-child(2) {
  opacity: 0;
}

.sidebar-toggle-btn.active .toggle-line:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -6px);
}

.desktop-sidebar {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  transform: translateX(0);
}

.desktop-sidebar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
}

/* Sidebar States */
.sidebar-collapsed {
  width: 80px !important;
}

.sidebar-mobile-open {
  transform: translateX(0) !important;
}

.sidebar-mobile-closed {
  transform: translateX(-100%) !important;
}

/* Collapse Button */
.collapse-btn {
  position: absolute;
  top: 50%;
  right: -15px;
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 0.8rem;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.collapse-btn:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.sidebar-header {
  position: relative;
  z-index: 2;
  padding: 2rem 2.5rem 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
}

.sidebar-collapsed .sidebar-header {
  padding: 1.5rem 1rem;
}

.sidebar-logo {
  width: 120px;
  height: auto;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.sidebar-logo:hover {
  transform: scale(1.05);
}

.logo-collapsed {
  width: 40px !important;
}

.sidebar-divider {
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  margin: 1.5rem 0 0;
}

.role-section {
  position: relative;
  z-index: 2;
  padding: 0 2.5rem 1.5rem;
}

.role-section {
  position: relative;
  z-index: 2;
  padding: 0 2.5rem 1.5rem;
  transition: all 0.3s ease;
}

.sidebar-collapsed .role-section {
  padding: 0 1rem 1rem;
}

.sidebar-nav {
  position: relative;
  z-index: 2;
  padding: 0 1.5rem;
  flex: 1;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.sidebar-collapsed .sidebar-nav {
  padding: 0 0.5rem;
}

.sidebar-collapsed .nav-text {
  opacity: 0;
  width: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sidebar-collapsed .nav-indicator {
  display: none;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-item {
  margin-bottom: 0.5rem;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 1rem 1rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  overflow: hidden;
}

.nav-link.collapsed {
  padding: 1rem 0.5rem;
  justify-content: center;
}

.nav-link.collapsed .nav-icon {
  margin-right: 0;
}

.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.nav-link:hover::before {
  left: 100%;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.nav-link.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.nav-link.active .nav-indicator {
  opacity: 1;
  transform: scaleY(1);
}

.nav-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.1rem;
}

.nav-text {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.nav-indicator {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 4px;
  height: 20px;
  background: white;
  border-radius: 2px;
  opacity: 0;
  transition: all 0.3s ease;
}

.logout-nav {
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.logout-link {
  background: rgba(220, 53, 69, 0.1);
  color: rgba(255, 255, 255, 0.9);
}

.logout-link:hover {
  background: rgba(220, 53, 69, 0.2);
  color: white;
}

.main-content {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 0;
  height: 100vh;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.content-expanded {
  margin-left: 0;
}

/* Mobile Sidebar Styles */
.mobile-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
}

.mobile-sidebar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 1rem 0;
}

.mobile-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1.5rem;
  text-align: center;
  color: white;
}

.mobile-logo {
  width: 100px;
  height: auto;
  filter: brightness(0) invert(1);
  margin-bottom: 1rem;
}

.mobile-menu {
  padding: 1rem 0;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.menu-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
  transition: left 0.5s ease;
}

.menu-item:hover::before {
  left: 100%;
}

.menu-item:hover {
  background: rgba(102, 126, 234, 0.05);
  transform: translateX(5px);
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  margin-right: 1rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.menu-item:hover .menu-icon {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.menu-text {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #2B2D42;
  letter-spacing: 0.5px;
}

.menu-arrow {
  color: #999;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.menu-item:hover .menu-arrow {
  color: #667eea;
  transform: translateX(3px);
}

.logout-item {
  margin-top: 1rem;
  border-top: 2px solid rgba(0, 0, 0, 0.05);
}

.logout-item .menu-icon {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.logout-item:hover .menu-icon {
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
}

/* Large Desktop (1440px+) */
@media (min-width: 1440px) {
  .sidebar-toggle-btn {
    display: none !important;
  }
  
  .desktop-sidebar {
    width: 360px !important;
    position: relative !important;
    transform: translateX(0) !important;
  }
  
  .sidebar-collapsed {
    width: 80px !important;
  }
  
  .sidebar-header {
    padding: 2.5rem 3rem 2rem;
  }
  
  .sidebar-logo {
    width: 140px;
  }
  
  .nav-link {
    padding: 1.25rem 1.5rem;
    font-size: 1.05rem;
  }
  
  .nav-icon {
    width: 28px;
    height: 28px;
    font-size: 1.2rem;
  }
  
  .nav-text {
    font-size: 1rem;
  }
  
  .main-content {
    padding-top: 0 !important;
  }
  
  .content-expanded {
    margin-left: 0 !important;
  }
}

/* Desktop (1024px - 1439px) */
@media (min-width: 1024px) and (max-width: 1439px) {
  .sidebar-toggle-btn {
    display: none !important;
  }
  
  .desktop-sidebar {
    width: 320px !important;
    position: relative !important;
    transform: translateX(0) !important;
  }
  
  .sidebar-collapsed {
    width: 80px !important;
  }
  
  .sidebar-header {
    padding: 2rem 2.5rem 1.5rem;
  }
  
  .sidebar-logo {
    width: 120px;
  }
  
  .main-content {
    padding-top: 0 !important;
  }
  
  .content-expanded {
    margin-left: 0 !important;
  }
}

/* Tablet Landscape (768px - 1023px) */
@media (min-width: 768px) and (max-width: 1023px) {
  .sidebar-toggle-btn {
    display: flex;
  }
  
  .desktop-sidebar {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: 280px !important;
    z-index: 999;
    transform: translateX(-100%);
  }
  
  .sidebar-mobile-open {
    transform: translateX(0);
  }
  
  .sidebar-mobile-closed {
    transform: translateX(-100%);
  }
  
  .collapse-btn {
    display: none;
  }
  
  .main-content {
    margin-left: 0 !important;
    padding-top: 80px;
  }
  
  .content-expanded {
    margin-left: 0;
  }
  
  .sidebar-header {
    padding: 1.5rem 2rem 1rem;
  }
  
  .sidebar-logo {
    width: 100px;
  }
  
  .nav-link {
    padding: 0.875rem 1rem;
  }
  
  .nav-text {
    font-size: 0.9rem;
  }
  
  .role-section {
    padding: 0 2rem 1rem;
  }
}

/* Mobile Landscape & Portrait (max-width: 767px) */
@media (max-width: 767px) {
  .sidebar-toggle-btn {
    display: flex;
    top: 15px;
    left: 15px;
    width: 45px;
    height: 45px;
  }
  
  .desktop-sidebar {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: 100% !important;
    max-width: 320px;
    z-index: 999;
    transform: translateX(-100%);
  }
  
  .sidebar-mobile-open {
    transform: translateX(0);
  }
  
  .collapse-btn {
    display: none;
  }
  
  .main-content {
    margin-left: 0 !important;
    padding-top: 75px;
  }
  
  .content-expanded {
    margin-left: 0;
  }
  
  .sidebar-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  
  .sidebar-logo {
    width: 90px;
  }
  
  .nav-link {
    padding: 0.875rem 1rem;
    margin: 0.25rem 0;
  }
  
  .nav-text {
    font-size: 0.9rem;
  }
  
  .nav-icon {
    width: 22px;
    height: 22px;
    font-size: 1rem;
    margin-right: 0.875rem;
  }
  
  .role-section {
    padding: 0 1.5rem 1rem;
  }
  
  .sidebar-nav {
    padding: 0 1rem;
  }
  
  /* Mobile Menu Enhancements */
  .mobile-container {
    padding: 0.5rem;
  }
  
  .mobile-header {
    padding: 1.5rem 1rem;
  }
  
  .mobile-logo {
    width: 80px;
  }
  
  .menu-item {
    padding: 1rem 1rem;
  }
  
  .menu-icon {
    width: 36px;
    height: 36px;
  }
  
  .menu-text {
    font-size: 0.95rem;
  }
}

/* Small Mobile (max-width: 480px) */
@media (max-width: 480px) {
  .sidebar-toggle-btn {
    width: 40px;
    height: 40px;
    top: 10px;
    left: 10px;
  }
  
  .toggle-line {
    width: 16px;
  }
  
  .desktop-sidebar {
    width: 100% !important;
    max-width: 280px;
  }
  
  .main-content {
    padding-top: 60px;
  }
  
  .sidebar-header {
    padding: 1.25rem 1rem 0.75rem;
  }
  
  .sidebar-logo {
    width: 70px;
  }
  
  .nav-link {
    padding: 0.75rem 0.875rem;
    border-radius: 8px;
  }
  
  .nav-text {
    font-size: 0.85rem;
  }
  
  .nav-icon {
    width: 20px;
    height: 20px;
    font-size: 0.9rem;
    margin-right: 0.75rem;
  }
  
  .role-section {
    padding: 0 1rem 0.75rem;
  }
  
  .sidebar-nav {
    padding: 0 0.75rem;
  }
  
  .sidebar-divider {
    margin: 1rem 0 0;
  }
}

/* Ultra Small Devices (max-width: 360px) */
@media (max-width: 360px) {
  .desktop-sidebar {
    max-width: 100%;
  }
  
  .sidebar-header {
    padding: 1rem 0.75rem 0.5rem;
  }
  
  .sidebar-logo {
    width: 60px;
  }
  
  .nav-link {
    padding: 0.625rem 0.75rem;
  }
  
  .nav-text {
    font-size: 0.8rem;
  }
  
  .nav-icon {
    width: 18px;
    height: 18px;
    font-size: 0.85rem;
    margin-right: 0.625rem;
  }
  
  .role-section {
    padding: 0 0.75rem 0.5rem;
  }
  
  .sidebar-nav {
    padding: 0 0.5rem;
  }
}

/* High DPI / Retina Displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .sidebar-logo,
  .mobile-logo {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
}

/* Landscape Orientation Adjustments for Mobile */
@media (max-width: 767px) and (orientation: landscape) {
  .sidebar-header {
    padding: 1rem 1.5rem 0.75rem;
  }
  
  .nav-link {
    padding: 0.625rem 1rem;
  }
  
  .sidebar-nav {
    padding: 0 1rem;
  }
  
  .main-content {
    padding-top: 60px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .sidebar-overlay {
    background: rgba(0, 0, 0, 0.7);
  }
  
  .collapse-btn {
    background: #1f2937;
    border-color: #4b5563;
    color: #9ca3af;
  }
  
  .collapse-btn:hover {
    background: #374151;
    color: #f3f4f6;
  }
}

/* Reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .desktop-sidebar,
  .nav-link,
  .sidebar-logo,
  .toggle-line,
  .collapse-btn,
  .main-content {
    transition: none;
  }
  
  .nav-link::before {
    transition: none;
  }
  
  .menu-item::before {
    transition: none;
  }
}

/* Animation for smooth transitions */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.nav-item {
  animation: slideIn 0.3s ease forwards;
}

.nav-item:nth-child(1) { animation-delay: 0.1s; }
.nav-item:nth-child(2) { animation-delay: 0.2s; }
.nav-item:nth-child(3) { animation-delay: 0.3s; }
.nav-item:nth-child(4) { animation-delay: 0.4s; }
.nav-item:nth-child(5) { animation-delay: 0.5s; }

.menu-item {
  animation: slideIn 0.4s ease forwards;
}

.menu-item:nth-child(1) { animation-delay: 0.1s; }
.menu-item:nth-child(2) { animation-delay: 0.2s; }
.menu-item:nth-child(3) { animation-delay: 0.3s; }
.menu-item:nth-child(4) { animation-delay: 0.4s; }
.menu-item:nth-child(5) { animation-delay: 0.5s; }

/* Default Desktop Styles (1024px+) */
@media (min-width: 1024px) {
  .sidebar-toggle-btn {
    display: none !important;
  }
  
  .desktop-sidebar {
    position: relative !important;
    transform: translateX(0) !important;
  }
  
  .main-content {
    padding-top: 0 !important;
  }
  
  .content-expanded {
    margin-left: 0 !important;
  }
  
  .sidebar-overlay {
    display: none !important;
  }
  
  /* Fix white background issue */
  .el-container {
    background: transparent !important;
  }
  
  .el-aside {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  }
}

/* Scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>